import type { PreferencesData } from 'src/api/procurer';
import type { ContractPreferencesCurrentStep as CurrentStep } from 'src/types';
import type { ContractMetaData } from 'src/pages/procurer/contract/Start/schema';

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useRouter } from 'src/routes/hooks';

import dayjs from 'src/utils/dayjs';
import { formatIsoAsDDMMYYYY } from 'src/utils/format-time';

import { usePreferencesStore } from 'src/store';
import { getPreferences } from 'src/api/procurer';
import { useContractSetupStore } from 'src/store/contractSetupStore';
import {
  getContract,
  createContract,
  updateContract,
  saveContractSetup,
  getContractByToken,
  updateAnnualBreakdown,
  type AnnualBreakdownEdit,
} from 'src/api/contract';

import { useSetupNavigation } from './useSetupNavigation';

export const useCreateContract = (contractId?: string, isOpenForTender?: boolean) => {
  const queryClient = useQueryClient();
  const setContractMetadata = useContractSetupStore((s) => s.setContractMetadata);
  const sessionId = useContractSetupStore((s) => s.sessionId);
  const router = useRouter();
  const mode = usePreferencesStore((s) => s.mode);
  const { navigateToNextStep } = useSetupNavigation(mode);
  const createContractMutation = useMutation({
    mutationFn: createContract,
  });
  const updateContractMutation = useMutation({
    mutationFn: updateContract,
  });

  const createContractMutateFn = async (
    data: ContractMetaData,
    setError?: (path: string, errorObj: any) => void,
    skipNavigation = false
  ) => {
    const mutationFn = contractId ? updateContractMutation : createContractMutation;
    try {
      const response = await mutationFn.mutateAsync({
        ...data,
        sessionId,
        isOpenForTender,
        ...(contractId ? { id: contractId } : {}),
      });
      queryClient.invalidateQueries({ queryKey: ['contract'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardContracts'] });
      queryClient.invalidateQueries({ queryKey: ['open-contracts'] });
      queryClient.invalidateQueries({
        queryKey: ['contract-open-for-tender', Number(contractId)],
      });
      queryClient.invalidateQueries({
        queryKey: ['contract-applications', Number(contractId)],
      });
      queryClient.removeQueries({ queryKey: ['previousDelivery', contractId] });
      const result = response.data as any;
      /**
       * Read from the `*Iso` aliases, not the bare columns: a DATE arrives as a JS Date and
       * serialises as an instant, which lands on the previous day for anyone behind the server. The
       * cut-off has to be a plain YYYY-MM-DD too, since it is echoed back as a boundary the server
       * compares against stored text.
       */
      const contractData = {
        ...result,
        contractValue: Number(result.contractValue),
        startDate: result.startDateIso ? formatIsoAsDDMMYYYY(result.startDateIso) : '',
        endDate: result.endDateIso ? formatIsoAsDDMMYYYY(result.endDateIso) : '',
        backfillCutoffDate: result.backfillCutoffDateIso ?? null,
        applicationStartDate: result.applicationStartDate
          ? dayjs(result.applicationStartDate).toISOString()
          : '',
        applicationEndDate: result.applicationEndDate
          ? dayjs(result.applicationEndDate).toISOString()
          : '',
        isNew: result.isExistingContract ? 'no' : 'yes',
        contractType: result.type ?? '',
        contractLeadName: result.leadName ?? '',
        contractLeadEmail: result.leadEmail ?? '',
      };
      setContractMetadata(contractData);
      if (!skipNavigation) {
        if (contractId) {
          navigateToNextStep('start');
        } else {
          router.push(`/procurer/contract/${response.data.id}/geography`);
        }
      }
    } catch (error: any) {
      if (typeof setError === 'function') {
        if (error.data?.issues) {
          error.data.issues.forEach((issue: any) => {
            setError(issue.path[0], { message: issue.message });
          });
        } else {
          setError('root', { message: error.message ?? 'Something went wrong' });
        }
      }
      throw error;
    }
  };

  const isPending = createContractMutation.isPending || updateContractMutation.isPending;

  return { createContractMutateFn, isPending };
};

export type ExistingContractData = {
  commitments?: any;
  requirements?: any;
  reportingData?: any;
  // #634: the previous-delivery step's only payload. null clears backfill mode.
  // Optional cut-off: omitting it leaves the stored one alone, so completing the step is not a
  // write. Only choosing a cut-off — or clearing it with null — sends the key.
  previousDelivery?: {
    backfillCutoffDate?: string | null;
    /**
     * The boundaries the page was showing, so the server can refuse a rebuild against a stale view.
     * All three, since a period boundary is derived from all three.
     */
    currentBackfillCutoffDate?: string | null;
    currentStartDate?: string | null;
    currentCommitmentPeriod?: string | null;
  };
};

export const useSaveContractSetup = () => {
  const { contractMetadata, requirements, reportingData, setErrors } = useContractSetupStore();
  const { geography, themes, impact, scoring, qualitative, finalizing, completedSteps, mode } =
    usePreferencesStore();
  const { navigateToNextStep } = useSetupNavigation(mode);
  const queryClient = useQueryClient();
  const sessionId = useContractSetupStore((s) => s.sessionId);

  const saveContractSetupMutation = useMutation({
    mutationFn: saveContractSetup,
  });

  const saveContractSetupMutateFn = async (
    currentStep: CurrentStep,
    contractId: string,
    overrideData?: PreferencesData & ExistingContractData,
    overrideSetError?: (path: string, errorObj: any) => void,
    skipNavigation = false
  ) => {
    const updatedCompletedSteps = [...new Set([...completedSteps, currentStep])];
    setErrors({});
    try {
      await saveContractSetupMutation.mutateAsync({
        id: contractId,
        sessionId,
        details: contractMetadata,
        currentStep,
        geography: {
          geographicalAreas: geography?.geographicalAreas ?? [],
          priorityGeographicalAreas: geography?.priorityGeographicalAreas ?? [],
        },
        themes,
        impact,
        scoring,
        qualitative,
        finalizing: finalizing ?? undefined,
        completedSteps: updatedCompletedSteps,
        requirements,
        reportingData,
        ...overrideData,
      });
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
      queryClient.invalidateQueries({ queryKey: ['dashboardContracts'] });

      const savedRrule =
        (overrideData as ExistingContractData | undefined)?.reportingData?.rrule ??
        reportingData?.rrule;
      if (savedRrule) {
        queryClient.removeQueries({ queryKey: ['previousDelivery', contractId] });
      }

      if (!skipNavigation) {
        navigateToNextStep(currentStep);
      }
    } catch (error: any) {
      if (error.data?.issues) {
        if (overrideSetError) {
          error.data.issues.forEach((issue: any) => {
            overrideSetError(issue.path[1], { message: issue.message });
          });
        } else {
          const errorsObj: Record<string, any> = {};
          error.data.issues.forEach((issue: any) => {
            if (!errorsObj[issue.path[0]]) {
              errorsObj[issue.path[0]] = {};
            }
            if (issue.path[1]) {
              errorsObj[issue.path[0]][issue.path[1]] = { message: issue.message };
            } else {
              errorsObj[issue.path[0]] = { message: issue.message };
            }
          });
          setErrors(errorsObj);
        }
      }
      throw error;
    }
  };

  return { saveContractSetupMutateFn, isPending: saveContractSetupMutation.isPending };
};

export const useUpdateAnnualBreakdown = (contractId: string) => {
  const queryClient = useQueryClient();
  const sessionId = useContractSetupStore((s) => s.sessionId);

  const mutation = useMutation({
    mutationFn: (edits: AnnualBreakdownEdit[]) =>
      updateAnnualBreakdown({ contractId, sessionId, edits }),
  });

  const updateAnnualBreakdownMutateFn = async (edits: AnnualBreakdownEdit[]) => {
    await mutation.mutateAsync(edits);
    queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
  };

  return { updateAnnualBreakdownMutateFn, isPending: mutation.isPending, error: mutation.error };
};

export const useLoadContract = ({
  contractId,
  enabled,
}: {
  contractId: string;
  enabled: boolean;
}) => {
  const { data } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => getContract(contractId),
    enabled,
  });
  const { setContractMetadata, setRequirements, setReportingData, incrementHydration } =
    useContractSetupStore();
  const incrementPreferencesHydration = usePreferencesStore((s) => s.incrementHydration);
  // get default preferences
  const { data: defaultPreferences } = useQuery({
    queryKey: ['procurer-preferences'],
    queryFn: () => getPreferences(),
    enabled,
  });

  const {
    setMode,
    setGeography,
    setThemes,
    setImpact,
    setScoring,
    setQualitative,
    setFinalizing,
    setCompletedSteps,
  } = usePreferencesStore();

  useEffect(() => {
    if (data && enabled) {
      setMode('contract');
      if (data.details) {
        const { rrule, ...details } = data.details;
        const contractMetadata = {
          ...details,
          rrule: rrule || undefined,
          contractValue: Number(details.contractValue),
          startDate: details.startDate ? dayjs(details.startDate).format('DD-MM-YYYY') : '',
          endDate: details.endDate ? dayjs(details.endDate).format('DD-MM-YYYY') : '',
          applicationStartDate: details.applicationStartDate
            ? dayjs(details.applicationStartDate).toISOString()
            : '',
          applicationEndDate: details.applicationEndDate
            ? dayjs(details.applicationEndDate).toISOString()
            : '',
        };
        setContractMetadata(contractMetadata as any);
        setRequirements(
          (data.additionalRequirements ?? []).map((r) => ({
            id: r.id,
            type: r.type,
            title: r.title,
            details: r.details,
            hasUnits: r.hasUnits,
            units: r.units,
            delivered: r.delivered,
          }))
        );
        setReportingData((prev) => ({
          ...prev,
          rrule: rrule || prev.rrule,
        }));
      }

      if (data.completedSteps) {
        setCompletedSteps(data.completedSteps);
      }

      const geography = {
        geographicalAreas: data.geography?.geographicalAreas?.length
          ? data.geography?.geographicalAreas
          : defaultPreferences?.geography?.geographicalAreas,
        priorityGeographicalAreas: data.geography?.priorityGeographicalAreas?.length
          ? data.geography?.priorityGeographicalAreas
          : defaultPreferences?.geography?.priorityGeographicalAreas,
      };
      if (geography) {
        const geographyData = {
          geographicalAreas: geography.geographicalAreas ?? [],
          priorityGeographicalAreas: geography.priorityGeographicalAreas ?? [],
        };
        setGeography(geographyData);
      }

      const isThemeStepCompleted = data.completedSteps?.includes('themes');
      const themes = isThemeStepCompleted ? data.themes : defaultPreferences?.themes;
      if (themes) {
        setThemes(themes);
      }

      const impact = data.impact?.activities?.length ? data.impact : defaultPreferences?.impact;
      if (impact) {
        setImpact(impact);
      }

      if (data.scoring) {
        const scoring = data.scoring.visibilityPreferences
          ? data.scoring
          : defaultPreferences?.scoring;
        setScoring(scoring as any);
      }

      const qualitative = data.qualitative || defaultPreferences?.qualitative;
      if (qualitative) {
        setQualitative(qualitative);
      }

      const finalizing = data.finalizing || defaultPreferences?.finalizing;
      if (finalizing) {
        setFinalizing(finalizing);
      }
      incrementHydration();
      incrementPreferencesHydration();
    }
  }, [
    enabled,
    data,
    setContractMetadata,
    setGeography,
    setThemes,
    setImpact,
    setFinalizing,
    setScoring,
    setQualitative,
    setCompletedSteps,
    setMode,
    defaultPreferences,
    setRequirements,
    setReportingData,
    incrementHydration,
    incrementPreferencesHydration,
  ]);
};

export const useGetContractByToken = (token: string) =>
  useQuery({
    queryKey: ['contract', token],
    queryFn: () => getContractByToken(token),
  });
